"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { IMAGE_ALLOWED_MIME_TYPES, IMAGE_UPLOAD_MAX_FILES } from "@stocks/shared";

export type PickedImageStatus = "idle" | "uploading" | "done" | "error";

export interface PickedImage {
  id: string;
  file: File;
  previewUrl: string;
  status: PickedImageStatus;
  error?: string;
}

const toPicked = (file: File): PickedImage => ({
  id: crypto.randomUUID(),
  file,
  previewUrl: URL.createObjectURL(file),
  status: "idle",
});

/**
 * 다중 이미지 선택 상태 (feature model).
 * 미리보기 URL 수명, 개수/형식 제한, 대표 이미지, 업로드 상태를 관리한다.
 */
export const useImagePicker = (initialFiles: File[] = []) => {
  const [images, setImages] = useState<PickedImage[]>(() =>
    initialFiles.slice(0, IMAGE_UPLOAD_MAX_FILES).map(toPicked),
  );
  const [primaryId, setPrimaryId] = useState<string | null>(
    () => images[0]?.id ?? null,
  );
  const imagesRef = useRef(images);
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  // 언마운트 시 남은 미리보기 URL 해제
  useEffect(
    () => () => imagesRef.current.forEach((i) => URL.revokeObjectURL(i.previewUrl)),
    [],
  );

  const addFiles = useCallback((files: File[]) => {
    const valid = files.filter((f) =>
      (IMAGE_ALLOWED_MIME_TYPES as readonly string[]).includes(f.type),
    );
    if (valid.length !== files.length) {
      toast.error("jpeg / png / webp 이미지만 추가할 수 있습니다.");
    }
    const room = IMAGE_UPLOAD_MAX_FILES - imagesRef.current.length;
    if (valid.length > room) {
      toast.error(`사진은 최대 ${IMAGE_UPLOAD_MAX_FILES}장까지 추가할 수 있습니다.`);
    }
    const added = valid.slice(0, Math.max(room, 0)).map(toPicked);
    if (!added.length) return;
    setImages((prev) => [...prev, ...added]);
    setPrimaryId((cur) => cur ?? added[0].id);
  }, []);

  const removeImage = useCallback((id: string) => {
    const target = imagesRef.current.find((i) => i.id === id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    const rest = imagesRef.current.filter((i) => i.id !== id);
    setImages(rest);
    setPrimaryId((cur) => (cur === id ? (rest[0]?.id ?? null) : cur));
  }, []);

  /** 모든 사진 제거 (새 입고를 시작할 때) */
  const clearImages = useCallback(() => {
    imagesRef.current.forEach((i) => URL.revokeObjectURL(i.previewUrl));
    setImages([]);
    setPrimaryId(null);
  }, []);

  const setStatus = useCallback(
    (id: string, status: PickedImageStatus, error?: string) =>
      setImages((prev) =>
        prev.map((i) => (i.id === id ? { ...i, status, error } : i)),
      ),
    [],
  );

  return {
    images,
    primaryId,
    setPrimaryId,
    addFiles,
    removeImage,
    clearImages,
    setStatus,
    files: images.map((i) => i.file),
  };
};
